import PropTypes from 'prop-types';
import QrScanner from 'qr-scanner';
import React, { useEffect, useState } from 'react';
import Cards, { getPlaceDisplayName, getRandomPlace } from '../../assets/cards/cards';
import { ReactComponent as BottomLeftCorner } from '../../assets/icons/corners/bottom_left.svg';
import { ReactComponent as BottomRightCorner } from '../../assets/icons/corners/bottom_right.svg';
import { ReactComponent as TopLeftCorner } from '../../assets/icons/corners/top_left.svg';
import { ReactComponent as TopRightCorner } from '../../assets/icons/corners/top_right.svg';
import PrimaryButton from '../../components/buttons/primarybutton/PrimaryButton';
import FeedbackButton from '../../components/feedbackbutton/FeedbackButton';
import Header from '../../components/header/Header';
import TipBubble from '../../components/tipbubble/TipBubble';
import { tag } from '../../services/ATInternetService';
import { getCollectionCount } from '../../stored/Collection';
import Page from '../Page';
import './Scanner.sass';


const OBJ_UPDATE_INTERVAL = 0.3 * 1000;
const QR_UPDATE_INTERVAL = 1.25 * 1000;

const MIN_SCAN_TIME = 2.75;

const video = document.getElementById('webcam__video');

const Scanner = ({ onCardPlaceScanned, onRequestShowCollection, onTradeQRScanned }) => {

	const [possibleCard, setPossibleCard] = useState(null);
	const [possibleCardString, setPossibleCardString] = useState('');
	const [scanStartTime, setScanStartTime] = useState(null);
	const [stopShowingObjectDetection, setStopShowingObjectDetection] = useState(false);
	const [hasPlayedSound, setHasPlayedSound] = useState(0);

	const stopRunningModel = scanStartTime !== null && scanStartTime + (MIN_SCAN_TIME - 1.2) <= new Date().valueOf() / 1000;
	const scanFinished = scanStartTime !== null && scanStartTime + (MIN_SCAN_TIME - 0.6) <= new Date().valueOf() / 1000;

	const percentage = possibleCard ? (((new Date().valueOf() / 1000) - scanStartTime)) * 30 : 0;

	const showFeedbackButton = false;

	useEffect(() => {
		const interval = setInterval(() => {
			setStopShowingObjectDetection(scanStartTime !== null && scanStartTime + MIN_SCAN_TIME <= new Date().valueOf() / 1000);

			(
				async () => {
					if (possibleCard !== null) {
						setHasPlayedSound(hasPlayedSound + 1);

						if (hasPlayedSound === 1) {
							// @ts-ignore
							window.vibrate(100);
							// @ts-ignore
							window.playSound('detected');
						} else if (hasPlayedSound === 3) {
							// @ts-ignore
							window.vibrate(200);
							// @ts-ignore
							window.playSound('detected_2');
						}
					} else {
						setHasPlayedSound(0);
						// @ts-ignore
						window.stopSound();
					}

					// @ts-ignore
					if (window.model && !stopRunningModel) runModel((data) => {
						const scannedPossibleCardData = findPossibleCards(data, possibleCard, scanStartTime);
						setPossibleCard(scannedPossibleCardData.possibleCard);
						setPossibleCardString(JSON.stringify(scannedPossibleCardData.possibleCard));
						setScanStartTime(scannedPossibleCardData.scanStartTime);
					});
				}
			)();
		}, OBJ_UPDATE_INTERVAL);
		return () => clearInterval(interval);
	}, [stopRunningModel, possibleCard, scanStartTime, hasPlayedSound]);

	useEffect(() => {
		const interval = setInterval(() => {
			searchForQR((value) => onTradeQRScanned(value));
		}, QR_UPDATE_INTERVAL);
		return () => clearInterval(interval);
	}, [stopRunningModel, possibleCard, scanStartTime, onTradeQRScanned]);

	useEffect(() => {
		if (stopShowingObjectDetection) {
			onCardPlaceScanned(possibleCard);
			// @ts-ignore
			window.playSound(possibleCard.match.class === 'apple' ? 'apple' : 'found');

		}
	}, [possibleCardString, possibleCard, stopShowingObjectDetection, onCardPlaceScanned]);

	const getScanLabel = () => {
		if (percentage < 10) return 'object...';
		if (percentage < 20) return `${getPlaceDisplayName(getRandomPlace())}?`;
		if (percentage < 30) return `${getPlaceDisplayName(getRandomPlace())}??`;
		if (percentage < 40) return `${getPlaceDisplayName(getRandomPlace())}...`;
		return `${getPlaceDisplayName(possibleCard.match.class)}!`;
	}

	const isPortraitMode = () => window.innerHeight > window.innerWidth;

	return <>
		<Page className="capture">
			<div
				className="capture__corners"
				style={possibleCard !== null ? {
					left: `${possibleCard.match.bbox[0]}px`,
					top: `${possibleCard.match.bbox[1]}px`,
					width: `${possibleCard.match.bbox[2]}px`,
					height: `${possibleCard.match.bbox[3]}px`,
					opacity: !scanFinished ? 1 : 0,
					transform: !scanFinished ? 'scale3d(1, 1, 1) rotate(-4deg)' : 'scale3d(6, 6, 6)',
					transition: `all ${OBJ_UPDATE_INTERVAL / 2}ms ease-in-out`,
				} : {
					left: isPortraitMode() ? '10vw' : '10vh',
					top: isPortraitMode() ? '10vh' : '10vh',
					width: isPortraitMode() ? '80vw' : 'calc(100vw - 20vh)',
					height: showFeedbackButton ? (isPortraitMode() ? 'calc(calc(80vh - 4rem) - 3.5rem)' : 'calc(80vh - 4rem)') : (isPortraitMode() ? 'calc(80vh - 3.5rem)' : '80vh'),
					opacity: 0.6,
					transform: 'scale3d(1, 1, 1)',
					transition: `all ${OBJ_UPDATE_INTERVAL / 2}ms ease-in-out`,
				}}>
				{possibleCard == null && <div className="capture__corners__dot h-6 w-6 rounded-full absolute right-6 top-6" />}

				<div
					className="capture__corners__scanner"
					style={possibleCard !== null ? {
						animation: `capture__corners__scanner ${MIN_SCAN_TIME}s ease-in-out forwards`,
					} : {
						animation: '',
					}}
				/>

				<TopLeftCorner className="capture__corners__corner" />
				<TopRightCorner className="capture__corners__corner" />
				<BottomLeftCorner className="capture__corners__corner" />
				<BottomRightCorner className="capture__corners__corner" />

				{possibleCard?.match && <div
					className="capture__label bg-white py-2 px-4 font-bold rounded-full shadow-md absolute text-center bottom-8 right-8 text-lg"
					style={possibleCard !== null ? {
						animation: `capture__label ${MIN_SCAN_TIME}s cubic-bezier(0.175, 0.885, 0.320, 1.275) forwards`,
					} : {
						animation: '',
					}}>
					{getScanLabel()}
				</div>}
			</div>
		</Page>
		{/* @ts-ignore */}
		{!window.model ?
			<Page className="flex flex-col content-center justify-center pb-16 transition-all duration-1000">
				<p className="bg-black text-white rounded-xl mx-8 shadow-md py-8 px-8 select-none text-center"><img className="h-24 object-contain mx-auto" src="/klokhuisscanner/logo128.gif?v1" alt="Logo" /><b className="text-lg">We zijn de Klokhuis Scanner aan het downloaden!</b><br /><br />Nog heel evenjes... Duurt het langer dan een minuut? Probeer dan opnieuw te laden op een betere internet verbinding.</p>
			</Page> :
			<Page
				className="flex flex-col content-end justify-end pb-16 transition-all duration-1000"
				style={{
					opacity: percentage < 20 ? 1 : 0,
					transform: `translateY(${!possibleCard ? '0' : '100%'})`,
				}}>
				<TipBubble animated soundEnabled={!possibleCard} />
				<PrimaryButton className={`self-center${showFeedbackButton ? ' mb-8' : ''}`} animated onClick={onRequestShowCollection}>
					{getCollectionCount() > 0 ? 'Verzameling' : 'Tips'}
				</PrimaryButton>
			</Page>}

		{/* @ts-ignore */}
		{showFeedbackButton && window.model && <Header className="absolute bottom-0 shadow-2xl"><FeedbackButton /></Header>}
	</>;
};

Scanner.propTypes = {
	onCardPlaceScanned: PropTypes.func.isRequired,
	onRequestShowCollection: PropTypes.func.isRequired,
	onTradeQRScanned: PropTypes.func.isRequired
}

const runModel = (setResults) => {
	// Limit to 6 objects at a time and a minimum confidence of 66%
	// @ts-ignore
	window.model.detect(video, 6, 0.66).then((predictions) => {
		const modelResults = [];

		// Now lets loop through predictions and draw them to the live view if
		// they have a high confidence score.
		for (let n = 0; n < predictions.length; n += 1) {
			// If we are over 66% sure, we are sure we classified it right
			if (modelResults.indexOf(predictions[n]) === -1) {
				predictions[n].bbox = transformBbox(predictions[n].bbox);
				modelResults.push(predictions[n]);
			} else {
				// Combine bounds of multiple objects of same class
				modelResults[modelResults.indexOf(predictions[n])].bbox = addBbox(modelResults[modelResults.indexOf(predictions[n])].bbox, transformBbox(predictions[n].bbox))
			}
		}

		setResults(modelResults);
	});
};

const findPossibleCards = (results, possibleCard, scanStartTime) => {
	let newCardPlaceFound = false;

	const possibleCards = [];
	if (results.length > 0) {
		Cards.forEach(card => {
			results.forEach(result => {
				if (card.places.join().includes(result.class)) {
					possibleCards.push({
						card,
						match: result,
					});
				}
			});
		});
	}

	if (possibleCards.length > 0) {
		if (possibleCard === null || !possibleCards.some(card => card.card === possibleCard.card)) {
			// Get random card
			[possibleCard] = possibleCards.sort(() => .5 - Math.random());
			scanStartTime = new Date().valueOf() / 1000;
			newCardPlaceFound = true;
		} else {
			possibleCard.match.bbox = possibleCards.find(card => card.card === possibleCard.card).match.bbox;
		}
	} else {
		possibleCard = null;
		scanStartTime = null;
	}

	return {
		possibleCard,
		scanStartTime,
		newCardPlaceFound,
	};
};

const transformBbox = (bbox) => {
	// @ts-ignore
	const scaleFactor = window.innerHeight / video.videoHeight;
	// @ts-ignore
	const fullWidth = video.videoWidth * scaleFactor;
	const horizontalCorrection = (fullWidth - window.innerWidth) / 2;

	bbox = bbox.map(point => point * scaleFactor);

	// Left & Right
	bbox[0] -= horizontalCorrection;
	bbox[2] -= horizontalCorrection;

	return bbox;
};

const addBbox = (original, added) => {
	original[0] = added[0] < original[0] ? added[0] : original[0];
	original[1] = added[1] > original[1] ? added[1] : original[1];
	original[2] = added[2] < original[2] ? added[2] : original[2];
	original[3] = added[3] > original[3] ? added[3] : original[3];

	return original;
};

const searchForQR = (onTradeQRScanned) => {
	QrScanner.WORKER_PATH = `${window.location.href.split('?')[0]}/qr-scanner-worker.min.js`;
	// @ts-ignore
	QrScanner.scanImage(video)
		.then(result => {
			const type = result.split(':')[0];
			const data = result.replace(`${type}:`, '');
			if (type === 'trading') {
				onTradeQRScanned(JSON.parse(data));
			}
			tag.sendClick('scanner', {
				chapter3: 'qr',
				name: 'scanned',
			});
		})
		.catch((error) => { if (error) { /* console.error(error); */ } });
}

export default Scanner;
